/**
 * Os textos do painel de comparação facial na tela (E87.F3, E90.F4, E116.F2, E117.F1).
 *
 * Saíram de `PainelDaConferencia.tsx` na E117.F1 para o componente caber no teto: são as mesmas
 * frases do documento emitido (`relatorio_eletronico_texto`, `linha_de_metodo`), e a suíte da E117
 * prende a igualdade com o backend.
 */
import { ptBR } from '@/i18n/pt-BR';
import { contagemDoPonto, type ConferenciaFacialParecerData } from '@/lib/conferencia_facial';
import { VEREDITO_ROTULO, type Veredito } from '@/lib/veredito';

const t = ptBR.exameEletronico.conferenciaFacial.painel;
const tl = ptBR.exameEletronico.conferenciaFacial.leituras;

/**
 * E117.F1 (pedido do owner, ADR-0079 E.19): o resultado de cada nível da escala de sete níveis no
 * vocabulário único do caso — +3 a +1 convergente, 0 inconclusivo, -1 a -3 divergente. "Não
 * avaliável" e a escala anterior ficam sem rótulo (o mesmo mapa de `RESULTADO_DO_DEGRAU`).
 */
const RESULTADO_DO_DEGRAU: Readonly<Record<string, Veredito>> = {
  mesma_origem_forte: 'convergente',
  mesma_origem_moderado: 'convergente',
  mesma_origem_limitado: 'convergente',
  inconclusivo: 'inconclusivo',
  origem_distinta_limitado: 'divergente',
  origem_distinta_moderado: 'divergente',
  origem_distinta_forte: 'divergente',
};

/** A frase do degrau precedida do resultado em maiúsculas, sem repetir o "inconclusivo". */
export function comResultado(degrau: string, frase: string): string {
  const veredito = RESULTADO_DO_DEGRAU[degrau];
  if (veredito === undefined) return frase;
  const resultado = VEREDITO_ROTULO[veredito].toUpperCase();
  const resto = frase.toLowerCase().startsWith(resultado.toLowerCase())
    ? frase.slice(resultado.length).replace(/^[\s,]+/, '')
    : frase;
  return `${resultado} — ${resto}`;
}

/** E117.F1 (X-02): em quantos elementos as leituras repetiram a descrição nas duas colunas. */
function textoDasColunasIdenticas(n: number): string | null {
  if (n <= 0) return null;
  const quantos = n === 1 ? t.umElemento : t.nElementos.replace('{n}', String(n));
  return t.colunasIdenticas.replace('{quantos}', quantos);
}

/**
 * A linha CONCLUSÃO: o degrau gravado, sem percentual (E.11), no vocabulário da escala de
 * suporte de sete níveis (E115.F1). Sem degrau (parecer anterior à migration 0092) sai a
 * leitura gravada e a ausência. A ressalva fixa sai em linha própria, abaixo (E115.F1): dentro
 * da frase ela enfraquecia a leitura da conclusão.
 */
export function textoDaConclusao(parecer: ConferenciaFacialParecerData): string {
  const degrau = parecer.compatibilidade;
  if (degrau === null) {
    return `${tl[parecer.leitura]}. ${t.degrauNaoRegistrado}`;
  }
  return `${comResultado(degrau, t.conclusaoDoDegrau[degrau])}.`;
}

/** E116.F2: o elemento em que as leituras não tiveram maioria é marcado, sem contagem. */
export function marcaSemMaioria(parecer: ConferenciaFacialParecerData, ponto: string): string {
  return contagemDoPonto(parecer.sustentacao, ponto) === 'sem_maioria' ? t.semMaioria : '';
}

function comK(texto: string, k: number, n?: number): string {
  return texto.replaceAll('{k}', String(k)).replace('{n}', String(n ?? k));
}

function textoDasLeituras(parecer: ConferenciaFacialParecerData): string | null {
  const s = parecer.sustentacao;
  if (s === null || s === undefined || s.k <= 1) return null;
  if (s.sem_maioria.includes('leitura')) return comK(t.semMaioriaGlobal, s.k);
  if (s.sem_maioria.includes('compatibilidade')) return comK(t.semMaioriaNoNivel, s.k);
  if (s.compatibilidade === s.k) return comK(t.porUnanimidade, s.k);
  return comK(t.porMaioria, s.k, s.compatibilidade);
}

const ESCALA_ANTERIOR: readonly string[] = ['alta', 'media_alta', 'media', 'baixa'];

const POR_EXTENSO = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito'];

/**
 * E209.F2.T08 (GUS-296 item 13c): quantos traços o painel compara — a conta do exame
 * (`tracos_comparados`): as linhas sem a expressão facial, nunca um número fixo. O parecer v2 compara
 * dezoito (18); o v1, oito. A expressão, quando registrada, é dita à parte.
 */
export function textoDosTracos(parecer: ConferenciaFacialParecerData): string {
  const tracos = parecer.painel.filter((linha) => linha.elemento !== 'expressao_facial');
  const n = tracos.length;
  const frase = t.tracosComparados.replace('{n}', POR_EXTENSO[n] ?? String(n));
  const expressao =
    tracos.length < parecer.painel.length ||
    parecer.ressalvas.some((r) => r.startsWith('expressão facial'));
  return expressao ? `${frase}${t.expressaoRegistrada}` : frase;
}

/**
 * E116.F2 (decisão do owner, 27/09; ADR-0079 E.17): a escala, as leituras e a cor fora da
 * leitura numa linha — a mesma do documento emitido (`linha_de_metodo`, teste de igualdade).
 */
export function textoDoMetodo(parecer: ConferenciaFacialParecerData): string {
  const antiga = ESCALA_ANTERIOR.includes(parecer.compatibilidade ?? '');
  const partes = [
    `${t.morfologiaInspirada}, ${textoDosTracos(parecer)}`,
    antiga ? t.escalaAnterior : t.escalaCurta,
    textoDasLeituras(parecer),
    parecer.cor_na_leitura === true ? t.corRegistrada : t.corForaDaLeitura,
    textoDasColunasIdenticas(parecer.colunas_identicas ?? 0),
  ].filter((parte): parte is string => parte !== null);
  return t.metodo.replace('{partes}', partes.join('; '));
}
